import React, { useState } from 'react';
import { Brain, TrendingUp, LineChart } from 'lucide-react';
import { generateFinancialInsights, suggestCostOptimizations } from '../../lib/openai';
import { useAIInsights } from '../../lib/store/hooks';
import type { AIInsightsPanelProps } from '../../lib/utils/types';
import { LoadingSpinner } from '../common/LoadingSpinner';

export function AIInsightsPanel({ financialData, expenses }: AIInsightsPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setAIInsights] = useAIInsights();

  const generateInsights = async () => {
    setLoading(true);
    setError(null);
    try {
      const [insightResults, optimizationResults] = await Promise.all([
        generateFinancialInsights(financialData),
        suggestCostOptimizations(expenses),
      ]);
      
      setAIInsights({
        insights: insightResults,
        optimizations: optimizationResults,
        loading: false,
        error: null
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate insights';
      console.error('Error generating AI insights:', error);
      setError(errorMessage);
      setAIInsights({
        insights: null,
        optimizations: null,
        loading: false,
        error: new Error(errorMessage)
      });
    } finally {
      setLoading(false);
    }
  };

  const [{ insights, optimizations }] = useAIInsights();

  return (
    <div className="bg-dark-card rounded-lg shadow p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Brain className="h-6 w-6 text-primary-400" aria-hidden="true" />
          <h2 className="text-xl font-semibold text-dark-text">AI Insights</h2>
        </div>
        <button
          onClick={generateInsights}
          disabled={loading}
          className="px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={loading ? 'Analyzing data...' : 'Generate insights'}
        >
          {loading ? (
            <>
              <LoadingSpinner size="sm" />
              <span>Analyzing...</span>
            </>
          ) : (
            <>
              <LineChart className="h-4 w-4" aria-hidden="true" />
              <span>Generate Insights</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {(insights || optimizations) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {insights && (
            <div className="bg-dark-bg rounded-lg p-4">
              <h3 className="text-lg font-medium text-dark-text mb-3 flex items-center space-x-2">
                <TrendingUp className="h-5 w-5 text-primary-400" aria-hidden="true" />
                <span>Financial Insights</span>
              </h3>
              <p className="text-dark-muted whitespace-pre-line">{insights}</p>
            </div>
          )}
          {optimizations && (
            <div className="bg-dark-bg rounded-lg p-4">
              <h3 className="text-lg font-medium text-dark-text mb-3 flex items-center space-x-2">
                <Brain className="h-5 w-5 text-primary-400" aria-hidden="true" />
                <span>Cost Optimizations</span>
              </h3>
              <p className="text-dark-muted whitespace-pre-line">{optimizations}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}